import React, { useEffect, useState } from "react";
import { Avatar, Box, IconButton, Menu, Tooltip } from "@mui/material";
import Settings from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import RateReviewIcon from "@mui/icons-material/RateReview";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { useCurrUserUtility } from "../contexts/useCurrUserUtility";
import { pageRouteUrls } from "../helper/pageRouteUrls";
import { useNavigate } from "react-router-dom";
import { GetColorBasedOnText, GetIniti } from "../helper/functions";
import { AccountInfoModel, UserSubscriptionStatusEnum, UserTypeEnum } from "../generated/api";
import { DateUtility } from "../helper/DateUtility";
import { MediamDateFormat } from "../helper/Constants";
import ReactCountryFlag from "react-country-flag"; 
import DashboardIcon from "@mui/icons-material/Dashboard";
import BookIcon from "@mui/icons-material/Book";
import VideocamIcon from "@mui/icons-material/Videocam";
import PeopleIcon from "@mui/icons-material/People";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import BarChartIcon from "@mui/icons-material/BarChart";
import BusinessIcon from "@mui/icons-material/Business";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import { MessagingService } from "../service/MessagingService";
import { TutorService } from "../service/TutorService";

export interface HeaderProps {}

// ── Menu item shape ───────────────────────────────────────────
interface MenuItem {
  label: string;
  sub?: string;
  url: string;
  icon: React.ReactNode;
  iconClass: string;
  badge?: React.ReactNode;
}
 
// ── Build role-based menu items ───────────────────────────────
// Mirrors the navigationLinks pattern from Navigationbar.tsx:
//   each role gets its own section array returned from this fn.
const buildMenuItems = (
  usr: AccountInfoModel,
  subStatus: UserSubscriptionStatusEnum,
  showSubStuff: boolean
): { main: MenuItem[]; extra: MenuItem[] } => {
  const type = usr.userType as UserTypeEnum;
   
  // ── Student (0) ───────────────────────────────────────────
  if (type === UserTypeEnum.Student) {
    return {
      main: [
        { label: "My Lectures",  sub: "Enrolled lectures",        url: pageRouteUrls.CourseMy(),               icon: <BookIcon />,        iconClass: "um-icon-blue"   },
        { label: "Following",    sub: "Followed Instructors",  url: pageRouteUrls.InstructorFollowList(),   icon: <BookIcon />,        iconClass: "um-icon-purple" },
        { label: "Favorites",    sub: "Saved courses & lectures",  url: pageRouteUrls.FavoriteLists(),   icon: <BookIcon />,        iconClass: "um-icon-purple" },
      ],
      extra: [
        { label: "Billing & Costs", sub: "Invoices and payments", url: pageRouteUrls.Billing(), icon: <AttachMoneyIcon />, iconClass: "um-icon-amber" }],
    };
  }
 
  // ── Tutor (1) ─────────────────────────────────────────────
  if (type === UserTypeEnum.Tutor) {
    const extra: MenuItem[] = [];
    if (showSubStuff) {
      extra.push(
        {  label: "Subscription", sub: "Manage your plan", url: pageRouteUrls.SubscriptionManagement(), icon: <AttachMoneyIcon />, iconClass: "um-icon-teal",
          badge: subStatus === UserSubscriptionStatusEnum.SubscripedAboutToExpired
            ? <span className="um-badge um-badge-amber">Expiring</span>
            : subStatus === UserSubscriptionStatusEnum.Subscriped
              ? <span className="um-badge um-badge-green">Active</span>
              : undefined,
        },
        { label: "Billing & Costs", sub: "Invoices and payments", url: pageRouteUrls.Billing(), icon: <AttachMoneyIcon />, iconClass: "um-icon-amber" }
      );
    }
    return {
      main: [ 
        { label: "My Courses & lectures",       sub: "Manage your courses & lectures",     url: pageRouteUrls.CourseMy(),         icon: <VideocamIcon />,            iconClass: "um-icon-blue"   },
        { label: "Students",         sub: "Your enrolled students",  url: pageRouteUrls.Search() + "/?types=student", icon: <PeopleIcon />, iconClass: "um-icon-teal"  }, // TODO
        { label: "Earnings",         sub: "Revenue overview",        url: pageRouteUrls.Payments(),         icon: <MonetizationOnIcon />,  iconClass: "um-icon-amber"  },
      ],
      extra,
    };
  }
 
  // ── Parent (2) ────────────────────────────────────────────
  if (type === UserTypeEnum.Parent) {
    return {
      main: [
        { label: "My Children",     sub: "Manage & track children",   url: pageRouteUrls.MyChildrenPage(),           icon: <ChildCareIcon />,   iconClass: "um-icon-amber"  },
        { label: "Subscriptions",   sub: "Children's enrollments",    url: pageRouteUrls.Payments(),             icon: <CreditCardIcon />,  iconClass: "um-icon-teal"   },
        { label: "Reports",         sub: "Progress & activity",       url: pageRouteUrls.ParentReports() , icon: <BarChartIcon />, iconClass: "um-icon-blue"   },
      ],
      extra: [ 
        { label: "Billing & Costs", sub: "Invoices and payments", url: pageRouteUrls.Billing(), icon: <AttachMoneyIcon />, iconClass: "um-icon-amber" }
        ],
    };
  }
 
  // ── Academy (3) ───────────────────────────────────────────
  if (type === UserTypeEnum.Academy) {
    return {
      main: [
        { label: "Dashboard",       sub: "Academy overview",          url: pageRouteUrls.Dashboard(),              icon: <DashboardIcon />,   iconClass: "um-icon-purple" }, 
        { label: "Manage Tutors",   sub: "Your invited tutors",       url: pageRouteUrls.InstructorFollowList(),   icon: <PeopleIcon />,      iconClass: "um-icon-teal"   },
        { label: "Invite Tutor",    sub: "Send an invitation",        url: pageRouteUrls.InstructorFollowList() + "/invite", icon: <SendIcon />, iconClass: "um-icon-blue" },
        { label: "Revenue",         sub: "Financial reports",         url: pageRouteUrls.Payments(),               icon: <MonetizationOnIcon />, iconClass: "um-icon-amber" },
        // { label: "Inbox",           sub: "Messages & notifications",  url: pageRouteUrls.Messages(),               icon: <EmailIcon />,       iconClass: "um-icon-blue"   },
      ],
      extra: [
        {  label: "Subscription", sub: "Manage your plan", url: pageRouteUrls.SubscriptionManagement(), icon: <AttachMoneyIcon />, iconClass: "um-icon-teal",
          badge: subStatus === UserSubscriptionStatusEnum.SubscripedAboutToExpired
            ? <span className="um-badge um-badge-amber">Expiring</span>
            : subStatus === UserSubscriptionStatusEnum.Subscriped
              ? <span className="um-badge um-badge-green">Active</span>
              : undefined,
        },
        { label: "Billing & Costs", sub: "Invoices and payments", url: pageRouteUrls.Billing(), icon: <AttachMoneyIcon />, iconClass: "um-icon-amber" }],
    };
  }
 
  // ── Admin (4) ─────────────────────────────────────────────
  if (type === UserTypeEnum.Admin) {
    return {
      main: [
        { label: "Verification",   sub: "Tutors & academies",    url: pageRouteUrls.InstructorVerificationList(), icon: <AssignmentIndIcon />, iconClass: "um-icon-red"    },
        { label: "Payments",       sub: "All transactions",       url: pageRouteUrls.Payments(),                   icon: <MonetizationOnIcon />, iconClass: "um-icon-amber"  },
        { label: "Users",          sub: "Manage & impersonate",   url: pageRouteUrls.AdminUsersList(),             icon: <PeopleAltIcon />,      iconClass: "um-icon-blue"   },
        { label: "Promo Codes",    sub: "Discounts & coupons",    url: pageRouteUrls.AdminPromoCodes(),            icon: <LocalOfferIcon />,     iconClass: "um-icon-teal"   },
        { label: "Feedback",       sub: "User reviews & ratings",  url: pageRouteUrls.AdminFeedback(),              icon: <RateReviewIcon />,     iconClass: "um-icon-purple" },
      ],
      extra: [],
    };
  }

  // ── Guest / fallback ──────────────────────────────────────
  return {
    main: [
      { label: "Inbox", sub: "Messages & notifications", url: pageRouteUrls.Messages(), icon: <EmailIcon />, iconClass: "um-icon-blue" },
    ],
    extra: [],
  };
};

// ── User-type badge config ────────────────────────────────────────────────────
const roleConfig: Record<number, { label: string; bg: string; color: string; border: string }> = {
    [UserTypeEnum.Student]:  { label: "Student",        bg: "#eff6ff", color: "#3b82f6", border: "#bfdbfe" },
    [UserTypeEnum.Tutor]:    { label: "Tutor",          bg: "#ededff", color: "#6378ff", border: "#c7d2fe" },
    [UserTypeEnum.Parent]:   { label: "Parent",         bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
    [UserTypeEnum.Academy]:  { label: "Academy",        bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
    [UserTypeEnum.Admin]:    { label: "Admin",          bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};
const academyTutorConfig = { label: "Academy Tutor", bg: "#e8f5e9", color: "#309255", border: "#a5d6a7" };

const getRoleConfig = (type?: UserTypeEnum, isAcademyTutor?: boolean) => {
    if (type === UserTypeEnum.Tutor && isAcademyTutor)
        return academyTutorConfig;
    return type !== undefined ? (roleConfig[type as number] ?? null) : null;
};

export const UserMenu = () => {
    const { getCurrUser, isAuth, setUser } = useCurrUserUtility();
    const [usr, setUsr] = useState<AccountInfoModel | undefined>(undefined);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [isAcademyTutor, setIsAcademyTutor] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const navigate = useNavigate();

    const open = Boolean(anchorEl);
    const handleClick = (event: any) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const editProfileUrl =
        usr?.userType === UserTypeEnum.Academy
            ? pageRouteUrls.UserEdit()
            : usr?.userType === UserTypeEnum.Tutor
                ? pageRouteUrls.InstructorEdit()
                : usr?.userType === UserTypeEnum.Parent
                    ? pageRouteUrls.ParentEdit()
                    : usr?.userType === UserTypeEnum.Student
                        ? pageRouteUrls.StudentEdit()
                        : "#";

    useEffect(() => {
        const currUser = getCurrUser();
        setUsr(currUser);
        // Load unread message count
        if (isAuth()) {
            new MessagingService().getUnreadCount().then(setUnreadMessages);
        }
        // Check academy tutor status
        if (currUser?.userType === UserTypeEnum.Tutor && currUser.token) {
            new TutorService(navigate).GetGraphByToken(currUser.token)
                .then(data => {
                    if ((data as any)?.academyToken) setIsAcademyTutor(true);
                })
                .catch(() => {});
        }
    }, []);
 
    const subStatus = usr?.userSubscriptionStatus ?? UserSubscriptionStatusEnum.NotSubscriptionValid;
    const showSubStuff = usr?.userType === UserTypeEnum.Tutor || usr?.userType === UserTypeEnum.Academy;

    // Build role-based items (mirrors Navigationbar.tsx pattern)
    const { main: mainItems, extra: extraItems } =
        usr ? buildMenuItems(usr, subStatus, showSubStuff) : { main: [], extra: [] };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
 
                .um-wrap { position: relative; display: inline-flex; }
                .um-dot {
                    position: absolute; bottom: 1px; right: 1px;
                    width: 10px; height: 10px;
                    background: #22c55e; border-radius: 50%; border: 2px solid #fff;
                }
                /* Unread messages count badge — top-right of avatar */
                .um-msg-badge {
                    position: absolute; top: -4px; right: -4px;
                    min-width: 16px; height: 16px;
                    background: #e53935; color: #fff;
                    border-radius: 20px; border: 1.5px solid #fff;
                    font-size: 9px; font-weight: 800;
                    display: flex; align-items: center; justify-content: center;
                    padding: 0 3px; line-height: 1;
                    pointer-events: none;
                    box-shadow: 0 1px 3px rgba(0,0,0,.2);
                }
                /* Role badge overlaid at bottom-left of avatar */
                .um-role-tag {
                    position: absolute; bottom: -4px; left: -4px;
                    font-size: 9px; font-weight: 800; letter-spacing: 0.02em;
                    padding: 1px 5px; border-radius: 20px;
                    border: 1.5px solid #fff;
                    line-height: 1.5;
                    white-space: nowrap;
                    box-shadow: 0 1px 3px rgba(0,0,0,.12);
                    pointer-events: none;
                }
 
                .um-paper {
                    font-family: 'DM Sans', sans-serif !important;
                    background: #fff !important;
                    border-radius: 16px !important;
                    border: 1px solid #e8ecf5 !important;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06) !important;
                    min-width: 300px !important;
                    overflow: hidden !important;
                    padding: 0 !important;
                    display: flex !important;
                    flex-direction: column !important;
                    max-height: calc(100vh - 80px) !important;
                }
                /* Scrollable middle section */
                .um-scroll-body {
                    overflow-y: auto;
                    overflow-x: hidden;
                    flex: 1;
                    min-height: 0;
                }
                .um-scroll-body::-webkit-scrollbar { width: 4px; }
                .um-scroll-body::-webkit-scrollbar-track { background: transparent; }
                .um-scroll-body::-webkit-scrollbar-thumb { background: #d1d5ef; border-radius: 4px; }
                .um-scroll-body::-webkit-scrollbar-thumb:hover { background: #a0a8c4; }
                /* Sign-out footer pinned at bottom */
                .um-footer {
                    border-top: 1px solid #f0f2f8;
                    flex-shrink: 0;
                    padding: 5px 8px;
                }
                .um-paper::before {
                    content: '';
                    position: absolute; top: 0; right: 18px;
                    width: 10px; height: 10px;
                    background: #fff;
                    border-top: 1px solid #e8ecf5;
                    border-left: 1px solid #e8ecf5;
                    transform: translateY(-50%) rotate(45deg);
                    z-index: 0;
                }
 
                .um-profile {
                    display: flex; align-items: center; gap: 12px;
                    padding: 16px 16px 14px;
                    text-decoration: none;
                    border-bottom: 1px solid #f0f2f8;
                    transition: background 0.15s;
                    border-radius: 16px 16px 0 0;
                    cursor: pointer;
                }
                .um-profile:hover { background: #f8f9fd; }
                .um-profile-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
                .um-profile-name { font-size: 14px; font-weight: 700; color: #1a1f36; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .um-profile-email { font-size: 11.5px; color: #8b93b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .um-profile-meta { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #a0a8c4; margin-top: 2px; flex-wrap: wrap; }
 
                .um-alert-wrap { padding: 8px 10px 2px; }
                .um-alert-box {
                    border-radius: 10px; padding: 9px 12px;
                    font-size: 12px; font-weight: 500;
                    display: flex; align-items: flex-start; gap: 8px; line-height: 1.45;
                }
                .um-alert-info    { background: #eff6ff; border: 1px solid #bfdbfe; color: #2563eb; }
                .um-alert-warning { background: #fffbeb; border: 1px solid #fde68a; color: #b45309; }
                .um-alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #16a34a; }
                .um-alert-box svg { font-size: 15px !important; flex-shrink: 0; margin-top: 1px; }
                .um-alert-link {
                    display: inline-block; margin-top: 5px; padding: 3px 10px;
                    border-radius: 6px; font-size: 11px; font-weight: 700;
                    background: rgba(99,120,255,0.1); color: #6378ff;
                    text-decoration: none; border: 1px solid rgba(99,120,255,0.2);
                    transition: background 0.15s;
                }
                .um-alert-link:hover { background: rgba(99,120,255,0.18); }
 
                .um-section { padding: 5px 8px; }
                .um-section + .um-section { border-top: 1px solid #f0f2f8; }
 
                .um-item {
                    display: flex; align-items: center; gap: 10px;
                    padding: 8px 10px; border-radius: 10px;
                    text-decoration: none; color: #3d4466;
                    font-size: 13px; font-weight: 500;
                    cursor: pointer; border: none; background: none;
                    width: 100%; text-align: left;
                    transition: background 0.15s, color 0.12s;
                }
                .um-item:hover { background: #f4f5fb; color: #1a1f36; }
                .um-item.um-danger { color: #dc2626; }
                .um-item.um-danger:hover { background: #fff5f5; color: #b91c1c; }
 
                .um-item-icon {
                    width: 30px; height: 30px; border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .um-item-icon svg { font-size: 15px !important; }
                .um-icon-purple { background: #ededff; color: #6378ff; }
                .um-icon-blue   { background: #eff6ff; color: #3b82f6; }
                .um-icon-teal   { background: #f0fdfa; color: #0d9488; }
                .um-icon-amber  { background: #fffbeb; color: #d97706; }
                .um-icon-red    { background: #fef2f2; color: #dc2626; }
                .um-icon-red    { background: #fff5f5; color: #dc2626; }
 
                .um-item-label { display: flex; flex-direction: column; }
                .um-item-sub   { font-size: 10.5px; color: #a0a8c4; font-weight: 400; }
 
                .um-badge {
                    margin-left: auto; font-size: 10px; font-weight: 700;
                    border-radius: 20px; padding: 2px 8px;
                }
                .um-badge-green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
                .um-badge-amber { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
                .MuiMenu-list{overflow-y: auto}
            `}</style>

            {/* ── Trigger ── */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={getCurrUser()?.name ?? ""} placement="bottom">
                    <IconButton
                        onClick={handleClick}
                        size="small"
                        sx={{ ml: 2, p: 0 }}
                        aria-controls={open ? "account-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                    >
                        <div className="um-wrap">
                            <Avatar
                                sx={{
                                    width: 40, height: 40,
                                    bgcolor: GetColorBasedOnText(getCurrUser()?.name),
                                    fontSize: "1rem",
                                    border: "2px solid #e8ecf5",
                                }}
                                alt={getCurrUser()?.name}
                                src={getCurrUser()?.profileUrl}
                            >
                                {GetIniti(getCurrUser()?.name ?? "")}
                            </Avatar>
                            <span className="um-dot" />
                            {/* Unread messages badge — top-right of avatar */}
                            {unreadMessages > 0 && (
                                <span className="um-msg-badge">
                                    {unreadMessages > 9 ? "9+" : unreadMessages}
                                </span>
                            )}
                            {/* Role badge — bottom-left of avatar */}
                            {(() => {
                                const rc = getRoleConfig(getCurrUser()?.userType as UserTypeEnum, isAcademyTutor);
                                return rc ? (
                                    <span className="um-role-tag"
                                        style={{ background: rc.bg, color: rc.color, borderColor: rc.border }}>
                                        {rc.label}
                                    </span>
                                ) : null;
                            })()}
                        </div>
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── Menu ── */}
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{ className: "um-paper", elevation: 0 }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                sx={{ mt: 1.5 ,
                }}
            >
                {/* Profile row */}
                {usr && (
                    <a href={editProfileUrl} className="um-profile" onClick={handleClose}>
                        <Avatar
                            alt={usr.name}
                            src={usr.profileUrl}
                            sx={{
                                width: 42, height: 42,
                                bgcolor: GetColorBasedOnText(usr.name),
                                fontSize: "1.1rem", flexShrink: 0,
                                border: "2px solid #e8ecf5",
                            }}
                        >
                            {GetIniti(usr.name)}
                        </Avatar>
                        <div className="um-profile-info">
                            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                <span className="um-profile-name">{usr.name}</span>
                                {(() => {
                                    const rc = getRoleConfig(usr.userType as UserTypeEnum, isAcademyTutor);
                                    return rc ? (
                                        <span style={{
                                            fontSize: 10, fontWeight: 700, flexShrink: 0,
                                            padding: "1px 7px", borderRadius: 20,
                                            background: rc.bg, color: rc.color,
                                            border: `1px solid ${rc.border}`,
                                        }}>
                                            {rc.label}
                                        </span>
                                    ) : null;
                                })()}
                            </div>
                            <span className="um-profile-email">{usr.email}</span>
                            {(usr.title || usr.countryName) && (
                                <div className="um-profile-meta">
                                    {usr.title && <span>{usr.title}</span>}
                                    {usr.title && usr.countryName && <span>·</span>}
                                    {usr.countryName && <span>{usr.countryName}</span>}
                                    {usr.countryCode && (
                                        <ReactCountryFlag
                                            countryCode={usr.countryCode}
                                            svg
                                            style={{ width: "1em", height: "1em" }}
                                            title={usr.countryName}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </a>
                )}

                {/* ── Scrollable body: alerts + menu items ── */}
                <div className="um-scroll-body">

                {/* Subscription alerts */}
                {(usr?.userType ===UserTypeEnum.Tutor || usr?.userType === UserTypeEnum.Academy) && subStatus === UserSubscriptionStatusEnum.NotTutor && (
                    <div className="um-alert-wrap">
                        <div className="um-alert-box um-alert-info">
                            <AssignmentIndIcon />
                            <div>
                                Want to teach?
                                <br />
                                <a href={pageRouteUrls.SubscriptionManagement()} className="um-alert-link" onClick={handleClose}>
                                    Become a Tutor →
                                </a>
                            </div>
                        </div>
                    </div>
                )}
                {(usr?.userType ===UserTypeEnum.Tutor || usr?.userType === UserTypeEnum.Academy) && subStatus === UserSubscriptionStatusEnum.NotSubscriped && (
                    <div className="um-alert-wrap">
                        <div className="um-alert-box um-alert-info">
                            <AttachMoneyIcon />
                            <div>
                                No active subscription.
                                <br />
                                <a href={pageRouteUrls.SubscriptionManagement()} className="um-alert-link" onClick={handleClose}>
                                    Subscribe →
                                </a>
                            </div>
                        </div>
                    </div>
                )}
                {(usr?.userType ===UserTypeEnum.Tutor || usr?.userType === UserTypeEnum.Academy) && subStatus === UserSubscriptionStatusEnum.SubscripedAboutToExpired && (
                    <div className="um-alert-wrap">
                        <div className="um-alert-box um-alert-warning">
                            <AttachMoneyIcon />
                            <div>
                                Expiring <strong>{DateUtility.Format(usr?.endDate, MediamDateFormat)}</strong>
                                <br />
                                <a href={pageRouteUrls.SubscriptionManagement()} className="um-alert-link" onClick={handleClose}>
                                    Renew →
                                </a>
                            </div>
                        </div>
                    </div>
                )}
                {(usr?.userType ===UserTypeEnum.Tutor || usr?.userType === UserTypeEnum.Academy) && subStatus === UserSubscriptionStatusEnum.Subscriped && (
                    <div className="um-alert-wrap">
                        <div className="um-alert-box um-alert-success">
                            <Settings />
                            <div>
                                Subscription <strong>active</strong>
                                <br />
                                <span style={{ fontSize: 10.5 }}>Expires {DateUtility.Format(usr?.endDate, MediamDateFormat)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main actions */}
                <div className="um-section"> 
                    {usr?.userType === UserTypeEnum.Tutor&&
                        <a href={editProfileUrl} className="um-item" onClick={handleClose}>
                            <span className="um-item-icon um-icon-blue"><PersonIcon /></span>
                            <span className="um-item-label">
                                <span>Profile</span>
                                <span className="um-item-sub">Edit tutor profile and account settings</span>
                            </span>
                        </a>
                    }
                    {usr?.userType === UserTypeEnum.Student &&
                        <a href={editProfileUrl} className="um-item" onClick={handleClose}>
                            <span className="um-item-icon um-icon-blue"><PersonIcon /></span>
                            <span className="um-item-label">
                                <span>Profile</span>
                                <span className="um-item-sub">Edit account settings</span>
                            </span>
                        </a>
                    }
                    {usr?.userType === UserTypeEnum.Parent &&
                        <a href={editProfileUrl } className="um-item" onClick={handleClose}>
                            <span className="um-item-icon um-icon-blue"><PersonIcon /></span>
                            <span className="um-item-label">
                                <span>Profile</span>
                                <span className="um-item-sub">Edit account settings</span>
                            </span>
                        </a>
                    }
                    {usr?.userType === UserTypeEnum.Academy &&
                        <a href={editProfileUrl} className="um-item" onClick={handleClose}>
                            <span className="um-item-icon um-icon-blue"><PersonIcon /></span>
                            <span className="um-item-label">
                                <span>Profile</span>
                                <span className="um-item-sub">Edit tutor profile and account settings</span>
                            </span>
                        </a>
                    }
                    
                    <a href={pageRouteUrls.Messages()} className="um-item" onClick={handleClose}>
                        <span className="um-item-icon um-icon-blue"><EmailIcon /></span>
                        <span className="um-item-label">
                            <span>Inbox</span>
                            <span className="um-item-sub">Messages & notifications</span>
                        </span>
                        {unreadMessages > 0 && (
                            <span className="um-badge" style={{
                                marginLeft: "auto",
                                background: "#e53935",
                                color: "#fff",
                                border: "none",
                                minWidth: 20,
                                textAlign: "center",
                            }}>
                                {unreadMessages > 99 ? "99+" : unreadMessages}
                            </span>
                        )}
                    </a>
                    
                    <a href={pageRouteUrls.UserSettings()} className="um-item" onClick={handleClose}>
                        <span className="um-item-icon" style={{ color: "#309255" }}><Settings fontSize="small" /></span>
                        <span className="um-item-label">
                            <span>Settings</span>
                            <span className="um-item-sub">Manage email preferences</span>
                        </span>
                    </a>

                </div>

                {/* Subscription links */}
                {/* {showSubStuff && (
                    <div className="um-section">
                        <a href={pageRouteUrls.SubscriptionManagement()} className="um-item" onClick={handleClose}>
                            <span className="um-item-icon um-icon-teal"><AttachMoneyIcon /></span>
                            <span className="um-item-label">
                                <span>Subscription</span>
                                <span className="um-item-sub">Manage your plan</span>
                            </span>
                            {usr?.userType ===UserTypeEnum.Tutor && subStatus === UserSubscriptionStatusEnum.SubscripedAboutToExpired && (
                                <span className="um-badge um-badge-amber">Expiring</span>
                            )}
                            {usr?.userType ===UserTypeEnum.Tutor && subStatus === UserSubscriptionStatusEnum.Subscriped && (
                                <span className="um-badge um-badge-green">Active</span>
                            )}
                        </a>
                        <a href={pageRouteUrls.Billing()} className="um-item" onClick={handleClose}>
                            <span className="um-item-icon um-icon-amber"><AttachMoneyIcon /></span>
                            <span className="um-item-label">
                                <span>Billing & Costs</span>
                                <span className="um-item-sub">Invoices and payments</span>
                            </span>
                        </a>
                    </div>
                )} */}

                {/* ── Extra items (e.g. Tutor subscription/billing) ── */}
                {extraItems.length > 0 && (
                    <div className="um-section">
                        {extraItems.map((item) => (
                            <a key={item.url} href={item.url} className="um-item" onClick={handleClose}>
                                <span className={`um-item-icon ${item.iconClass}`}>{item.icon}</span>
                                <span className="um-item-label">
                                    <span>{item.label}</span>
                                    {item.sub && <span className="um-item-sub">{item.sub}</span>}
                                </span>
                                {item.badge}
                            </a>
                        ))}
                    </div>
                )}

                {/* ── Main role-based items ────────────────────────── */}
                {mainItems.length > 0 && (
                    <div className="um-section">
                        {mainItems.map((item) => (
                            <a key={item.url} href={item.url} className="um-item" onClick={handleClose}>
                                <span className={`um-item-icon ${item.iconClass}`}>{item.icon}</span>
                                <span className="um-item-label">
                                    <span>{item.label}</span>
                                    {item.sub && <span className="um-item-sub">{item.sub}</span>}
                                </span>
                                {item.badge}
                            </a>
                        ))}
                    </div>
                )}
 
 
                </div>{/* /um-scroll-body */}

                {/* ── Profile + Sign out — pinned footer ── */}
                <div className="um-footer"> 
                    <button
                        className="um-item um-danger"
                        onClick={() => { setUser(undefined); navigate(pageRouteUrls.Login()); handleClose(); }}
                    >
                        <span className="um-item-icon um-icon-red"><Logout /></span>
                        <span>Sign out</span>
                    </button>
                </div>
  
            </Menu>
        </>
    );
};